import React from 'react';
import { CartItem } from '../../utils/cart';
import Colors from '../../utils/color';
import { EntityMenuItem } from '../../reducers/messagingSlice';

/**
 * HITL-1757 — how a modifier count reads on the right pane: `4x cream`.
 *
 * - Before the name, with a plain `x`, as the POS cart already prints
 *   "2x hot coffee".
 * - `readBack` blue, bold: the one thing on the card that says "more than one".
 * - Only from 2. A line at 1 prints no count.
 *
 * Alignment is padding, not `1x`: an item with ANY count of 2 or more reserves
 * the gutter on every one of its modifier lines, so `decaf` starts where `cream`
 * does in `4x cream`. An item with no count reserves nothing and reads exactly
 * as it did before counts existed.
 */
export const MODIFIER_COUNT_GUTTER_PX = 20;

export const ModifierCountGutter = ({ quantity }: { quantity: number }) => (
  <span
    data-testid="modifier-count"
    style={{
      display: 'inline-block',
      width: MODIFIER_COUNT_GUTTER_PX,
      marginRight: 4,
      color: Colors.readBack,
      fontWeight: 700,
    }}
  >
    {quantity > 1 ? `${quantity}x` : ''}
  </span>
);

/** Does any modifier on this item, at any depth, carry a count of 2 or more? */
export const hasCountedModifier = (cartItem: CartItem): boolean =>
  Object.values(cartItem.childModifierGroups ?? {}).some((group) =>
    Object.values(group.selectedItems ?? {}).some(
      (selected) => (selected.quantity ?? 1) > 1 || hasCountedModifier(selected)
    )
  );

/** The same question for the AI's hypothesis item. */
export const hasCountedEntity = (children: EntityMenuItem[]): boolean =>
  children.some(
    (child) =>
      (child.quantity ?? 1) > 1 || hasCountedEntity(child.children ?? [])
  );
